// src/pages/Moderation/UsersViolationsPage/UsersViolationsPage.tsx
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import './UsersViolationsPage.scss';

import { PageHeader } from './ui/PageHeader';
import { UserViolationsDrawer } from './ui/UserViolationsDrawer';
import { UsersFilters } from './ui/UsersFilters';
import { UsersQuickStats } from './ui/UsersQuickStats';
import { UsersViolationsTable } from './ui/UsersViolationsTable';
import { useUsersViolations } from './ui/useUsersViolations';

import { useSelector } from 'react-redux';
import { getModerationReportsRequest } from 'redux/reducers/moderation/reducer';
import { getModerationReportsSelector } from 'redux/reducers/moderation/selectors';
import type { GetReportsResponse, ReportInfo } from 'redux/reducers/moderation/types';

const UsersViolationsPage: React.FC = () => {
  const dispatch = useDispatch();

  const {
    users,
    total,
    counters,
    topRestrictions,
    restrictionsOptions,
    flags,

    page,
    perPage,
    filters,
    sort,

    setPage,
    setPerPage,
    setFilters,
    setSort,
  } = useUsersViolations();

  const [selectedUser, setSelectedUser] = useState<any | null>(null);

  // ✅ локально під репорт для Drawer
  const [drawerReport, setDrawerReport] = useState<ReportInfo | null>(null);
  const [drawerLoading, setDrawerLoading] = useState(false);

  // ✅ читаємо reports з redux (використаємо як відповідь на запит)
  const reportsState = useSelector(getModerationReportsSelector) as GetReportsResponse | null;

  // ✅ коли selectedUser змінюється — тягнемо 1 репорт
  useEffect(() => {
    const userId = Number(selectedUser?.id);
    if (!userId || !Number.isFinite(userId)) {
      setDrawerReport(null);
      return;
    }

    setDrawerLoading(true);
    setDrawerReport(null);

    dispatch(
      getModerationReportsRequest({
        current_page: 1,
        per_page: 1, // ✅ беремо 1 (останній/перший — залежить від бекенду)
        user_id: userId,
        has_restriction: true,
      }),
    );
  }, [dispatch, selectedUser?.id]);

  // ✅ як тільки redux приніс data — кладемо в drawerReport
  useEffect(() => {
    if (!drawerLoading) return;

    const list: ReportInfo[] = Array.isArray(reportsState?.data) ? reportsState!.data : [];
    setDrawerReport(list[0] ?? null);
    setDrawerLoading(false);
  }, [reportsState]);

  return (
    <div className="users-violations-page">
      <div className="users-violations-page__content">
        <PageHeader
          title="Users with violations"
          subtitle="Users who have restrictions / reports"
        />

        <UsersQuickStats
          totalUsers={total}
          flags={flags}
          usersWithRestrictions={users.filter((u) => (u.restrictions?.length ?? 0) > 0).length}
          counters={counters}
          topRestrictions={topRestrictions}
          onPickRestriction={(r) =>
            setFilters((prev) => ({
              ...prev,
              restriction: r,
              onlyWithRestrictions: true,
            }))
          }
        />

        <UsersFilters
          value={filters}
          sort={sort}
          flags={flags}
          restrictionOptions={restrictionsOptions}
          onChange={setFilters}
          onSortChange={setSort}
          onReset={() =>
            setFilters({
              search: '',
              restriction: undefined,
              flag: undefined,
              dateFrom: undefined,
              dateTo: undefined,
              status: undefined,
            })
          }
        />

        <div className="users-violations-page__grid">
          <UsersViolationsTable
            users={users}
            total={total}
            page={page}
            flags={flags}
            perPage={perPage}
            onRowClick={(u) => setSelectedUser(u)}
            onPageChange={setPage}
            onPerPageChange={(pp) => {
              setPerPage(pp);
              setPage(1);
            }}
          />

          {selectedUser ? (
            <UserViolationsDrawer
              user={selectedUser}
              flags={flags}
              reportInfo={drawerReport} // ✅ репорт з message_data
              reportLoading={drawerLoading} // ✅ покажемо лоадер
            />
          ) : (
            <div className="uv-drawer" style={{ opacity: 0.7 }}>
              <div className="uv-drawer__title">User details</div>
              <div className="uv-drawer__row">
                <span className="uv-drawer__k">Tip</span>
                <span className="uv-drawer__v">Click a row to see details</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UsersViolationsPage;
